import { MutableRefObject, useCallback, useEffect } from 'react';
import { css } from '@emotion/react';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { NakedButton, Text, useModalControl } from '@frontend/design-system';
import { WeaveSupportLink } from '../../../weave-support-link';
import { type ConfigItem, useLineKeyMenuConfig } from '../hooks/menu-config';
import { ContactsExtensionsModal } from '../modals/contacts-extensions.modal';
import { UsersExtensionsModal } from '../modals/users-extensions.modal';
import { useLineKeyContext, useLineKeyState } from '../store/line-key-provider';
import { isHandheldLayout } from '../utils';
import { cardIndexStyle, cardStyle } from './card';

export const AddNewLineKeyCard = ({ containerRef }: { containerRef: MutableRefObject<HTMLElement | null> }) => {
  const { t } = useTranslation('phone', { keyPrefix: 'line-keys' });
  const { isMenuBarOpen, setIsMenuBarOpen, isKeysMaxedOut, finalLineKeys } = useLineKeyState([
    'isMenuBarOpen',
    'setIsMenuBarOpen',
    'isKeysMaxedOut',
    'finalLineKeys',
  ]);
  const { maxKeys, model } = useLineKeyContext();
  const { modalProps: userExtensionModalProps, triggerProps: usersExtensionTriggerProps } = useModalControl();
  const { modalProps: contactsExtensionModalProps, triggerProps: contactsExtensionTriggerProps } = useModalControl();

  const { config } = useLineKeyMenuConfig({
    openUserExtensionsModal: usersExtensionTriggerProps.onClick,
    openContactsModal: contactsExtensionTriggerProps.onClick,
  });

  const isHandheldDeviceLayout = isHandheldLayout(model);
  const handheldMaxKeys = model?.handheldKeys?.maxKeys ?? maxKeys;
  const effectiveMaxKeys = isHandheldDeviceLayout ? Math.min(maxKeys, handheldMaxKeys) : maxKeys;
  const hasNoAvailableSlot = isKeysMaxedOut(effectiveMaxKeys);

  useEffect(() => {
    if (hasNoAvailableSlot) setIsMenuBarOpen(false);
  }, [hasNoAvailableSlot]);

  const scrollToBottom = useCallback(() => {
    setIsMenuBarOpen(true);
    setTimeout(() => {
      containerRef?.current?.scroll({
        behavior: 'smooth',
        top: containerRef.current?.scrollHeight,
      });
    }, 100);
  }, [containerRef?.current]);

  const handleAddNewCard = (lineKey: ConfigItem) => {
    lineKey.onClick();
    scrollToBottom();
  };

  return (
    <>
      <div css={{ display: 'flex' }}>
        <Text as='div' weight='bold' css={cardIndexStyle}>
          {hasNoAvailableSlot ? '' : Object.keys(finalLineKeys ?? {}).length + 1}
        </Text>
        <div
          tabIndex={0}
          css={[
            cardStyle,
            css`
              ${isMenuBarOpen && `padding: 0;`}
              ${!isMenuBarOpen &&
              !hasNoAvailableSlot &&
              `&:hover {
                   border: 1px solid var(--fabric-color-border-interactive);
              }`}
            `,
          ]}
          id='line-key-add-new-card-menu'
        >
          {!isMenuBarOpen && (
            <>
              <NakedButton onClick={scrollToBottom} disabled={!!hasNoAvailableSlot}>
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <Icon name='plus' fontWeight={'600'} color={hasNoAvailableSlot ? 'disabled' : 'primary'} />
                  <Text
                    css={css`
                      margin-left: var(--fabric-space-gap-xs);
                    `}
                    weight='bold'
                    color={hasNoAvailableSlot ? 'disabled' : 'primary'}
                  >
                    {t('Add Line Keys')}
                  </Text>
                </div>

                <Text
                  css={{ marginTop: 'var(--fabric-space-gap-xs)' }}
                  textAlign='left'
                  size='medium'
                  color={hasNoAvailableSlot ? 'disabled' : 'inherit'}
                >
                  {t('Customize what you can do with the next Line Keys by choosing a Line Key type')}
                </Text>
              </NakedButton>
            </>
          )}
          {isMenuBarOpen && (
            <div
              css={[
                css`
                  position: relative;

                  & > div {
                    padding: var(--fabric-space-padding-xs) var(--fabric-space-padding-md);
                  }

                  & > div:not(:last-of-type) {
                    border-bottom: 1px solid var(--fabric-color-neutral-20);
                  }

                  & > div:not(:first-of-type) {
                    cursor: pointer;
                  }
                `,
              ]}
            >
              <Text color='light' as='div'>
                {t('Choose Line Key Type')}
              </Text>
              {config.map((lineKey, i) => {
                return (
                  <div
                    key={i}
                    css={css`
                      ${lineKey.disabled && 'pointer-events: none;'}
                      ${!lineKey.disabled &&
                      `&:hover {
                        background-color: var(--fabric-color-neutral-20);
                      }`}
                    `}
                    onClick={() => handleAddNewCard(lineKey)}
                  >
                    <Text color={lineKey.disabled ? 'disabled' : 'default'} weight='bold'>
                      {t('{{name}}', { name: lineKey.title })}
                    </Text>
                    <Text color={lineKey.disabled ? 'disabled' : 'default'} size='medium'>
                      {t('{{name}}', { name: lineKey.subtitle })}
                    </Text>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      {hasNoAvailableSlot && !isMenuBarOpen && (
        <Text css={{ marginLeft: '40px' }} color='light' size='medium'>
          <Trans t={t} maxKeys={effectiveMaxKeys}>
            This device has reached its maximum of {{ maxKeys }} Line Keys. The additional Line Keys will not appear on
            your desk phone. Contact <WeaveSupportLink fontWeight='regular' /> if you have an expansion module.
          </Trans>
        </Text>
      )}
      {userExtensionModalProps.show && (
        <UsersExtensionsModal scrollToBottom={scrollToBottom} {...userExtensionModalProps} />
      )}
      <ContactsExtensionsModal scrollToBottom={scrollToBottom} {...contactsExtensionModalProps} />
    </>
  );
};
